import { ChangeEvent, forwardRef, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { FormControl, Loader, ToggleSwitch, Typography } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import type { OtherSettingsCancellationPolicyFormHandle } from 'src/modules/user/components/settings-profile-info/other-settings-form-handles'
import { CancellationPolicyCoverage } from '../cancellation-policy-coverage'
import { CancellationPolicyFields } from '../cancellation-policy-fields'
import { CancellationPolicyResetModal } from '../cancellation-policy-reset-modal'
import { PolicyConfirmModal } from '../policy-confirm-modal'
import type { CancellationPolicySettingsSectionProps } from './cancellation-policy-settings-section.interface'
import classes from './cancellation-policy-settings-section.module.scss'
import { useCancellationPolicySettingsSection } from './use-cancellation-policy-settings-section'

const DESCRIPTION =
  'Set a notice window for cancellations. Clients who attempt to cancel inside the window are either warned or prevented from cancelling. If warned, you get a list of late cancels to follow up on.'
const CONFIRMATION_OFF_NOTE =
  'With Appointment Confirmation off, clients cannot cancel online or by SMS; the policy is still shown on the booking page, confirmation page and email, and still governs reschedules.'
const OFF_HELP = 'Custom policies on appointment types still apply.'

function TurnOnModal({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <PolicyConfirmModal
      title="Turn on the cancellation policy?"
      cancelLabel="Not now"
      confirmLabel="Turn on policy"
      onCancel={onCancel}
      onConfirm={onConfirm}
      testId="cancellation-policy-turn-on-modal"
    >
      <p>It applies right away to every upcoming appointment it covers, including appointments created before today.</p>
    </PolicyConfirmModal>
  )
}

export const CancellationPolicySettingsSection = forwardRef<
  OtherSettingsCancellationPolicyFormHandle,
  CancellationPolicySettingsSectionProps
>(function CancellationPolicySettingsSection(
  { userId, appointmentConfirmationEnabled, reschedulingEnabled, onDirtyChange },
  ref,
) {
  const { formatMessage } = useIntl()
  const section = useCancellationPolicySettingsSection({ userId, onDirtyChange }, ref)
  const { policyForm, counts } = section
  const enabled = useWatch({ control: policyForm.form.control, name: 'enabled' })
  const [isTurnOnOpen, setIsTurnOnOpen] = useState(false)
  const rescheduleSettingLabel = formatMessage({ id: 'settings.appointment.allowRescheduleFromConfirmation' })

  if (section.policyQuery.isLoading) return <Loader isLoading={true} />
  if (!section.policyQuery.data) return null

  const showLateCancelsLink = !!counts && (enabled || counts.showNav)

  return (
    <section className={classes.section} aria-label="Cancellation policy" data-testid="cancellation-policy-section">
      <Typography variant="h5" className={classes.title}>
        Cancellation policy
      </Typography>
      <Typography variant="body" className={classes.description}>
        {DESCRIPTION}
      </Typography>
      {!appointmentConfirmationEnabled && (
        <Typography variant="body" className={classes.note} data-testid="cancellation-policy-confirmation-off-note">
          {CONFIRMATION_OFF_NOTE}
        </Typography>
      )}
      <FormControl label="Account cancellation policy" labelClassName={classes.label}>
        <div className={classes.row}>
          <ToggleSwitch
            checked={enabled}
            aria-label="Account cancellation policy"
            data-testid="cancellation-policy-enabled"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              e.target.checked ? setIsTurnOnOpen(true) : policyForm.turnOff()
            }
          />
          <Typography variant="body" className={classes.stateLabel}>
            {enabled ? 'Enabled' : 'Disabled'}
          </Typography>
        </div>
      </FormControl>
      {!enabled && (
        <Typography variant="body" className={classes.help}>
          {OFF_HELP}
        </Typography>
      )}
      {enabled && (
        <div className={classes.enabledBlock}>
          <CancellationPolicyFields
            control={policyForm.form.control}
            errors={policyForm.errors}
            fieldRefs={section.fieldRefs}
            rescheduleNotApplicableNote={
              reschedulingEnabled ? null : `This doesn't apply while "${rescheduleSettingLabel}" is off.`
            }
            rescheduleSettingLabel={rescheduleSettingLabel}
            onNoticeChange={policyForm.onNoticeChange}
            onTextChange={policyForm.onTextChange}
          />
          <CancellationPolicyCoverage typesUsingAccount={section.typesUsingAccount} totalTypes={section.totalTypes} />
          {section.affectedTypes.length > 0 && (
            <button type="button" className={classes.link} onClick={section.openReset}>
              Reset all appointment types to the account policy
            </button>
          )}
        </div>
      )}
      {showLateCancelsLink && (
        <Link
          to={routeNames.lateCancels.path}
          className={classes.link}
          data-testid="cancellation-policy-late-cancels-link"
        >
          {`View late cancels (${counts.open} open)`}
        </Link>
      )}
      {isTurnOnOpen && (
        <TurnOnModal
          onCancel={() => setIsTurnOnOpen(false)}
          onConfirm={() => {
            policyForm.confirmTurnOn()
            setIsTurnOnOpen(false)
          }}
        />
      )}
      {section.isResetOpen && (
        <CancellationPolicyResetModal
          types={section.affectedTypes}
          isLoading={section.isResetting}
          onCancel={section.closeReset}
          onConfirm={() => void section.confirmReset()}
        />
      )}
    </section>
  )
})
